import { useState, useCallback, useEffect } from "react";
import {
  BlockStack,
  Box,
  Button,
  Combobox,
  Icon,
  InlineStack,
  Listbox,
  Text,
} from "@shopify/polaris";
import { SearchIcon, XSmallIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetch as fetchUsers, fetchById } from "services/user-select.service";

type UserRow = { id: string; name?: string; email?: string };

type Option = {
  value: string;
  /** Shown in dropdown (can include email to disambiguate) */
  listLabel: string;
  /** Shown in the input bar: name only, never email */
  nameOnly: string;
};

function nameOnlyForDisplay(u: UserRow): string {
  const n = u.name && String(u.name).trim();
  if (n) {
    return n;
  }
  return String(u.id);
}

type Props = {
  value: string;
  onChange: (userId: string) => void;
  /** Shown like a field label (e.g. "User") */
  label: string;
  onUserNameResolved?: (name: string) => void;
};

/**
 * Activity log: one search field; when a user is selected, their **name** (no email)
 * appears inside the left part of the bar with ×; type to search on the right.
 */
export function AuditLogUserSearchField({ value, onChange, label, onUserNameResolved }: Props) {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedName, setSelectedName] = useState("");

  const fetchOptions = useCallback(
    async (searchText: string) => {
      if (!companyId) {
        return;
      }
      setLoading(true);
      try {
        const data = await fetchUsers(companyId, { textSearch: searchText });
        const collection = (data?.usersCollection ?? data?.users ?? []) as UserRow[];
        setOptions(
          collection.map((u) => {
            const listLabel = u.name || u.email || u.id;
            return {
              value: u.id,
              listLabel,
              nameOnly: nameOnlyForDisplay(u),
            };
          })
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    if (companyId) {
      void fetchOptions("");
    }
  }, [companyId, fetchOptions]);

  useEffect(() => {
    if (!value || !companyId) {
      setSelectedName("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { userEntity } = await fetchById(value, companyId);
        if (cancelled) {
          return;
        }
        const resolvedName = nameOnlyForDisplay(userEntity as UserRow);
        setSelectedName(resolvedName);
        if (onUserNameResolved) {
          onUserNameResolved(resolvedName);
        }
      } catch {
        if (!cancelled) {
          setSelectedName("");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value, companyId, onUserNameResolved]);

  const handleInputChange = (input: string) => {
    setInputValue(input);
    const trimmed = input.trim();
    if (trimmed.length >= 2 || trimmed.length === 0) {
      void fetchOptions(trimmed);
    }
  };

  const handleSelect = (selected: string) => {
    const found = options.find((o) => o.value === selected);
    if (found) {
      setSelectedName(found.nameOnly);
      onChange(selected);
      if (onUserNameResolved) {
        onUserNameResolved(found.nameOnly);
      }
      setInputValue("");
    }
  };

  const handleClear = () => {
    onChange("");
    setSelectedName("");
    if (onUserNameResolved) {
      onUserNameResolved("");
    }
    setInputValue("");
  };

  const listMarkup = options.map((option) => (
    <Listbox.Option
      key={option.value}
      value={option.value}
      selected={value === option.value}
      accessibilityLabel={option.listLabel}
    >
      {option.listLabel}
    </Listbox.Option>
  ));

  const ph =
    t("user_search") !== "user_search"
      ? t("user_search")
      : "Search user...";

  return (
    <BlockStack gap="200">
      {label && (
        <Text as="span" variant="bodySm" fontWeight="semibold">
          {label}
        </Text>
      )}
      <Box
        background="bg-surface"
        borderColor="border"
        borderStyle="solid"
        borderWidth="025"
        borderRadius="200"
      >
        <InlineStack gap="0" wrap={false} blockAlign="stretch">
          {value ? (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 1,
                  minWidth: 0,
                  paddingLeft: 12,
                  paddingRight: 4,
                  maxWidth: "50%",
                }}
              >
                <InlineStack
                  gap="150"
                  wrap={false}
                  blockAlign="center"
                >
                  <Text as="span" variant="bodyMd" truncate>
                    {selectedName || "…"}
                  </Text>
                  <Button
                    icon={XSmallIcon as any}
                    variant="tertiary"
                    onClick={handleClear}
                    accessibilityLabel={t("clear", { defaultValue: "Clear" })}
                  />
                </InlineStack>
              </div>
              <div
                style={{
                  width: 1,
                  flexShrink: 0,
                  alignSelf: "stretch",
                  background: "var(--p-color-border)",
                  marginTop: 6,
                  marginBottom: 6,
                }}
                aria-hidden
              />
            </>
          ) : null}
          <div
            style={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Combobox
              allowMultiple={false}
              activator={(
                <Combobox.TextField
                  variant="borderless"
                  prefix={<Icon source={SearchIcon as any} />}
                  onChange={handleInputChange}
                  label={label || "User search"}
                  labelHidden
                  value={inputValue}
                  placeholder={ph}
                  autoComplete="off"
                />
              )}
            >
              <Listbox onSelect={handleSelect}>
                {loading ? (
                  <Listbox.Loading accessibilityLabel="Loading" />
                ) : (
                  <>{listMarkup}</>
                )}
              </Listbox>
            </Combobox>
          </div>
        </InlineStack>
      </Box>
    </BlockStack>
  );
}
